import gsap from 'gsap';
import Swiper from 'swiper';
import { Autoplay, EffectFade, Scrollbar } from 'swiper/modules';
import { createApp, onMounted, onUnmounted, ref, watch } from 'vue';

import { ANIMATION_CONFIGS } from '~/constants/animations';
import * as DataDogLogs from '~/lib/datadog/datadog';

import { useVideo } from '../composables/use-video';
import { useViewportSize } from '../composables/use-viewport-size';
import { BaseSection } from './base-section';

/**
 * @type {import('swiper/types').SwiperOptions}
 */
const SWIPER_SETTINGS = {
  allowTouchMove: false,
  autoplay: {
    delay: 5000,
    disableOnInteraction: false,
  },
  effect: 'fade',
  modules: [Autoplay, Scrollbar, EffectFade],
  slidesPerView: 1,
  speed: 1,
};

/**
 * Autoplay Carousel Section
 *
 * This section displays a carousel of videos.
 * The section uses Vue.js to handle slide navigation and play/pause functionality.
 *
 * @extends BaseSection
 * @constructor
 * @param {HTMLElement} container - The container element for the section
 */
class AutoplayCarousel extends BaseSection {
  constructor() {
    super('[data-section-type="autoplay-carousel"]');
  }

  init(instanceData) {
    const container = instanceData.container;
    const videos = container.querySelectorAll('video');
    const enableAutoplay = container.dataset.enableAutoplay === 'true';
    const sliderCount = container.querySelectorAll('.swiper-slide').length;

    if (!container) {
      DataDogLogs.error(
        '[Allbirds section] - Autoplay Carousel - init - Container not found',
        {},
        new Error('Container not found'),
      );
      return;
    }

    if (!videos?.length) {
      DataDogLogs.error(
        '[Allbirds section] - Autoplay Carousel - init - Videos not found',
        {},
        new Error('Videos not found'),
      );
      return;
    }

    const app = createApp({
      name: 'AutoplayCarouselSection',
      setup() {
        const carousel = ref(null);
        const isPlaying = ref(true);
        const videoRefs = ref({});
        const { isMobile } = useViewportSize();

        const handlePillsMobileAnimation = (index) => {
          const pillsSizes = [...container.querySelectorAll('.pill')].map(
            (pill) => pill.offsetWidth,
          );
          const offset = pillsSizes.reduce((acc, val, _index) => {
            if (_index < index) {
              acc += val;
              if (index) acc += 8;
            } else if (_index === index) {
              acc += val / 2;
            }
            return acc;
          }, 0);
          container.querySelector('.pills').style.left = `calc(50% - ${offset}px)`;
        };

        const togglePlayPause = (play) => {
          const videoId =
            carousel.value.slides[carousel.value.activeIndex].querySelector('video').id;
          if (!videoId) return;
          isPlaying.value = play ?? !isPlaying.value;
          carousel.value.autoplay[isPlaying.value ? 'resume' : 'pause']();
          videoRefs.value[videoId]?.togglePlayPause(isPlaying.value);
        };

        const slideTo = (evt) => {
          const nextSlideId = evt.target.dataset.slideId;
          const currentSlideId = carousel.value.slides[carousel.value.activeIndex].dataset.slideId;

          if (nextSlideId !== currentSlideId) {
            const nextSlideIndex = carousel.value.slides.findIndex(
              (slide) => slide.dataset.slideId === nextSlideId,
            );

            // Fill the previous pills and reset the selected one
            carousel.value.slides.forEach((slide, index) => {
              const slideId = slide.dataset.slideId;
              const pill = container.querySelector(`[data-slide-id="${slideId}"]`);
              if (index < nextSlideIndex) {
                pill.style.backgroundSize = '100% auto';
              } else {
                pill.style.backgroundSize = '0 auto';
              }
            });

            carousel.value.slideTo(nextSlideIndex, 1);
          }
        };

        const resetPills = () => {
          container.querySelectorAll('.pill').forEach((pill) => {
            pill.style.backgroundSize = '0 auto';
          });
        };

        const initSwiper = () => {
          // Destroy the existing carousel instance if it exists
          carousel.value?.destroy();

          // Reset the videos to the beginning
          container.querySelectorAll('video').forEach((video) => {
            video.currentTime = 0;
          });

          resetPills();

          // If mobile, first center the first pill, and then display all of them
          if (isMobile.value) {
            handlePillsMobileAnimation(0);
          } else {
            container.querySelector('.pills').style.left = '';
          }

          // Initialize Swiper
          carousel.value = new Swiper(container.querySelector('.swiper'), {
            ...SWIPER_SETTINGS,
            on: {
              autoplayTimeLeft: function ({ activeIndex, autoplay, slides }, timeLeft) {
                const slide = slides[activeIndex];
                const slideId = slide.dataset.slideId;
                const videoDuration = +slide.dataset.swiperAutoplay;
                if (!timeLeft || timeLeft === SWIPER_SETTINGS.autoplay.delay) {
                  timeLeft = videoDuration;
                }
                const percentage = 100 - (timeLeft * 100) / videoDuration;
                container.querySelector(`[data-slide-id="${slideId}"]`).style.backgroundSize =
                  `${percentage > 99.75 ? 100 : percentage}% auto`;
                if (percentage > 99.75 && sliderCount === 1) {
                  autoplay.stop();
                  slides[activeIndex].querySelector('video').currentTime = 0;
                  autoplay.start();
                }
              },
              slideChange: function ({ activeIndex, slides }) {
                if (!activeIndex) {
                  resetPills();
                }
                slides[activeIndex].querySelector('video').currentTime = 0;
                togglePlayPause(true);
                isMobile.value && handlePillsMobileAnimation(activeIndex);
              },
            },
          });

          // Pause the current video if autoplay is disabled
          if (!enableAutoplay) togglePlayPause(false);
        };

        const getVideoDuration = (video) =>
          new Promise((resolve) => {
            const duration = video?.duration;
            if (duration) {
              resolve(Math.round(duration * 1000));
            } else {
              video.addEventListener(
                'loadeddata',
                (event) => {
                  resolve(Math.round(event.target.duration * 1000));
                },
                { once: true },
              );
            }
          });

        onMounted(() => {
          videos.forEach((video) => {
            const videoId = video.id;
            if (!videoId) return;
            const videoComposable = useVideo(videoId);
            videoComposable.init();
            videoRefs.value[videoId] = videoComposable;
          });

          new IntersectionObserver((entries) => {
            const isVisible = entries[0].isIntersecting;

            if (isVisible && !carousel.value) {
              container.querySelectorAll('video').forEach(async (video, index) => {
                const slide = video.parentNode?.parentNode;
                const duration = await getVideoDuration(video);
                if (!slide || !duration) return;

                // Set autoplay delay for each slide based on the videos' durations
                slide.setAttribute('data-swiper-autoplay', duration);

                // Initialize the Swiper when it comes into view and the delay for the first slide (based on video duration) is set.
                index === 0 && initSwiper();
              });
            }
          }).observe(container);
        });

        watch(isMobile, () => {
          initSwiper();
        });

        onUnmounted(() => {
          carousel.value?.destroy();
          carousel.value = null;
        });

        return {
          isPlaying,
          slideTo,
          togglePlayPause,
          videoRefs,
        };
      },
    });
    app.mount(container);
  }

  /**
   * Sets up the animation timeline for the section that will play when it comes into view
   * This will be called automatically during initialization
   *
   * @param {Object} instanceData - The section instance data
   * @return {Object} The GSAP timeline for the animation
   */
  setupInViewAnimationTimeline(instanceData) {
    const container = instanceData.container;
    const video = container.querySelectorAll('video');
    const pills = container.querySelector('.pills');
    const playPauseButton = container.querySelector('.play-pause-button');

    if (!container || !video) {
      DataDogLogs.error(
        '[Allbirds section] - Autoplay Carousel - setupInViewAnimationTimeline - Missing elements for in-view animation',
        {},
        new Error('Missing elements for in-view animation'),
      );
      return null;
    }

    const tl = gsap.timeline({
      paused: true,
      scrollTrigger: {
        ...this.getInViewAnimationOptions(),
        trigger: container,
      },
    });

    if (!tl) {
      return null;
    }

    tl.to(video, ANIMATION_CONFIGS.FADE_IN, 0);
    pills && tl.to(pills, ANIMATION_CONFIGS.FADE_IN, 0.3);
    playPauseButton && tl.to(playPauseButton, ANIMATION_CONFIGS.FADE_IN, 0.3);

    return tl;
  }
}

document.addEventListener('DOMContentLoaded', () => {
  new AutoplayCarousel();
});
